import { createAsyncThunk } from '@reduxjs/toolkit';
import api from 'api';
import axios, { AxiosResponse } from 'axios';
import prepareDocuments from 'helpers/prepareDocuments';
import { IDocument, IDocumentInfo, IFolder, IWithWebkitPath, DocumentFilters, DocumentFilterKey } from 'interfaces';
import { cleanDocumentViewUrl } from 'helpers/documentFiltersUrl';
import { RootState } from 'store';
import dayjs from 'dayjs'
import {
  setIsActiveContextMenu,
  setIsDeletingDocuments,
  setIsLoadingProcess,
  setIsOpenDeleteEntityModal,
  setIsOpenInfoEntityModal,
  setIsOpenMoveEntityModal,
  setIsOpenNewVersionModal,
  setIsOpenPostfixModal,
  setIsOpenRenameEntityModal,
  setIsOpenShareEntityModal,
  setIsUploadingDocuments,
  setIsVisibleUploadModal,
  setMoreInfoHidden
} from 'store/slices/windowStateSlice';
import {
  addFileForDownloading,
  cleanFileList,
  removeDownloadedFile,
  setCanceledUploads,
  setChoosenDocuments,
  setDataDocumentTable,
  setDocuments,
  setNestedDocumentsAndFolders,
  setDocumentsType,
  setEntityOfContextMenu,
  setFolderFilesCount,
  setIsAddedViaDragger,
  
  setSelectedDocument,
  setSelectedDocumentsIds,
  setSelectedRowKeys,

  setUploadAbortController,
  setUploadingDocuments,
  setUploadingFolders,
  setViewType,
  setLastMovedEvent,
  setExpandedFoldersCache,
  setAppliedFilters,
  setStatusFilterOptions,
} from 'store/slices/dataDocumentsSlice';
import { setRoomAllChats, setSelectedChatRoom } from 'store/slices/chatsSlice';
import { setRooms } from 'store/slices/userDataSlice';
import { TFunction } from 'i18next';
import { resetViewerSlice, setDocumentToViewNull } from 'store/slices/viewerSlice';
import { setDataRoom } from 'store/slices/dataRoomSlice';
import { setFoldersForPermissions, setUserPermissions } from 'store/slices/dataPermissionsSlice';
import { RcFile } from 'antd/es/upload';
import { setNotes, setSelectedNote } from 'store/slices/dataNotes';
import { NavigateFunction } from 'react-router';
import { getDownloadMessage } from 'routes/Documents/Documents';
import segregateToRootFolders from 'helpers/segregateToRootFolders';
import buildFoldersTree from 'helpers/buildFoldersTree';
import fetchDocumentsData, { addNewFolder } from './documentsSocketsCreator';
import { MessageInstance } from 'antd/es/message/interface';

import { encodeFiltersToBase64 } from 'helpers/encodeFiltersToBase64';

const buildFiltersParam = (filters?: DocumentFilters): { filters?: string } => {
  if (!filters) return {};
  const base64Str = encodeFiltersToBase64(filters);
  return base64Str ? { filters: base64Str } : {};
};

export const fetchAvailableDocuments = createAsyncThunk(
  'dataDocuments/fetchAll',
  async (props: { roomId: string, folderId?: string | null, filters?: DocumentFilters }, thunkAPI) => {
    try {
      const { roomAllChats } = (thunkAPI.getState() as RootState).chat;
      const source = axios.CancelToken.source();
      thunkAPI.signal.addEventListener('abort', () => source.cancel());

      const rootState = thunkAPI.getState() as RootState;
      const { userFoldersPermissions, foldersForPermissions } = rootState.permissions;
      const { currentFolder } = rootState.documents;

      const folders = props.folderId
        ? await api.getFoldersOfFolder(props.folderId, source.token)
        : await api.getFolders(props.roomId, source.token);        

      const targetFilters = props.filters ?? rootState.documents.appliedFilters;

      const documents = await api.getDocuments(
        { room_id: props.roomId, folder_id: props.folderId || undefined, action: 'view', ...buildFiltersParam(targetFilters) },
        source.token
      );

      const foldersWithPermissions = folders.data.map(folder => {
        const targetFolderId = props.folderId ? currentFolder[0]?.id : folder.id;
        const currentPermissions = userFoldersPermissions?.find(folder => folder.id === targetFolderId)?.permissions
          || foldersForPermissions?.find(folder => folder.id === targetFolderId)?.permissions
        return {
          ...folder,
          permissions: currentPermissions,
        }
      });      
              
      // if (!props.folderId) {
      //   thunkAPI.dispatch(setFoldersForPermissions(foldersWithPermissions.map(folder => ({...folder, key: `folder ${folder.id}`, type: 'folder'}))))
      // }

      const documentChats = roomAllChats
        .filter(({ entity_type, unviewed_amount }) => entity_type === 0 && unviewed_amount > 0)
        .map(({ entity_id }) => entity_id);

      const preparedDocuments = documents.data.map((document: IDocument) => ({
        ...document,
        upload_at: dayjs(document.upload_at).valueOf(),
        updated_at: dayjs(document.upload_at).valueOf(),
        clicked: false,
        key: document.id,
        // unviewed_amount: documentChats.includes(document.id) ? 1 : 0,
      }));
      const dataForDocumentTable = prepareDocuments(preparedDocuments, foldersWithPermissions);

      return {
        documents: preparedDocuments, dataForDocumentTable: dataForDocumentTable, folders: foldersWithPermissions || []
      };
    } catch (e) {      
      return thunkAPI.rejectWithValue('Download failed');
    }
  }
);

export const deleteTargetEntity = createAsyncThunk(
  'deleteDocument/delete',
  async (props: {
    entity: IDocument | IFolder | null,
    t: TFunction,
    navigate: () => void,
    message: (text: string) => JSX.Element,
  }, thunkApi) => {
    const { dispatch } = thunkApi;
    const rootState = thunkApi.getState() as RootState;
    const { dataRoom } = rootState.dataRoom;
    const { roomAllChats } = rootState.chat;
    const { documents, folders, nestedDocuments, nestedFolders } = rootState.documents;
    const { available_rooms } = rootState.userData;
    const { messageApi } = rootState.windowState;

    dispatch(setIsDeletingDocuments(true));
    try {
      if (props.entity?.type !== 'folder') {
        const targetDoc = props.entity as IDocument;
        const isNewFile = targetDoc?.is_new;
        await api.deleteDocuments([targetDoc?.id!], {
          room_id: dataRoom?.id!,
          details: {
            documents_names: targetDoc?.id,
            documents_ids: targetDoc?.id,
          }
        });
        const chatsDeleteAt = roomAllChats.map((chat) => chat.entity_id === targetDoc?.id
          ? {...chat, deleted_at: dayjs().utc().format('YYYY-MM-DDTHH:mm:ss.SSSSSS')}
          : chat
        );
        dispatch(setRoomAllChats(chatsDeleteAt));
        dispatch(setDocuments({
          documents: documents.filter(doc => doc.id !== targetDoc?.id),
          folders: folders
        }));
        dispatch(setNestedDocumentsAndFolders({
          documents: nestedDocuments.filter(doc => doc.id !== targetDoc?.id),
          folders: nestedFolders,
        }));
        dispatch(setRooms(available_rooms.map((room) => room.id === dataRoom?.id
          ? {
              ...room,
              documents_amount: Math.max(0, (room.documents_amount || 0) - 1),
              new_count: isNewFile ? Math.max(0, (room.new_count || 0) - 1) : room.new_count,
            }
          : room
        )));
        messageApi?.success(props.message(props.t('Documents.modals.delete.successFile')), 8);
      } else {
        await api.deleteFolder(props.entity?.id!);
        const foldersFilered = folders.filter(folder => folder.id !== props.entity?.id);
        dispatch(setDocuments({
          documents: documents,
          folders: foldersFilered,
        }));
        dispatch(setNestedDocumentsAndFolders({
          documents: nestedDocuments,
          folders: nestedFolders.filter(folder => folder.id !== props.entity?.id),
        }));
        dispatch(setDataRoom({...dataRoom!, folders: foldersFilered}));
        dispatch(setFoldersForPermissions(foldersFilered));
        messageApi?.success(props.message(props.t('Documents.modals.delete.successFolder')), 8);
      }
    } catch (e) {      
      messageApi?.error(props.t('Documents.modals.delete.error'));
    } finally {
      dispatch(setIsDeletingDocuments(false));
      dispatch(setIsOpenDeleteEntityModal(false));
      dispatch(setSelectedChatRoom(null));
      dispatch(setDefaultView());
      props.navigate();
    }
  }
);

export const setDefaultView = createAsyncThunk(
  'setDefaultView/set',
  async (_, thunkApi) => {
    const { dispatch } = thunkApi;
   
    dispatch(setViewType('Table'));
    dispatch(setSelectedDocument(null));
    dispatch(setDocumentToViewNull());
  }
);

export const checkFileNames = createAsyncThunk(
  'checkFileNames/check',
  async (props: {
    allFiles: RcFile[];
    setDuplicateDocuments: (value: React.SetStateAction<{
      forUpload: RcFile[];
      forDelete: IDocument[];
      generatedNames: string[];
    }>) => void
  }, thunkApi) => {
    const { dispatch } = thunkApi;
    const rootState = thunkApi.getState() as RootState;
    const { dataRoom } = rootState.dataRoom;
      
    dispatch(setIsUploadingDocuments(true));

    try {
      const response = await Promise.all(
        props.allFiles.map((file) => api.validateDocuments({ document_name: file.name, room_id: dataRoom?.id! }))
      );
      const forDeleteFiles = response.reduce(
        (accum, documentResponse) => [...accum, ...documentResponse.data.documents],
        [] as IDocument[]
      );
      const generatedNames = response.map((documentResponse) =>
        documentResponse.data.documents?.length ? documentResponse.data.generated_name : ''
      );
      if(forDeleteFiles.length){
        props.setDuplicateDocuments({
          forDelete: forDeleteFiles,
          forUpload: props.allFiles,
          generatedNames,
        });
        dispatch(setIsOpenPostfixModal(true));
        dispatch(setIsVisibleUploadModal(false));
        return { hasDuplicates: true };
      }
      return { hasDuplicates: false };
    } catch (e) {
      return { hasDuplicates: false };
    } finally {
      dispatch(setIsUploadingDocuments(false));
    }
  }
);

export const requestDeleteEntity = createAsyncThunk(
  'requestDeleteEntity/check',
  async (props: {
    entity: IDocument | IFolder
  }, thunkApi) => {
    const { dispatch } = thunkApi;
    const rootState = thunkApi.getState() as RootState;
    const { dataDocumentTable } = rootState.documents;

    if (props.entity.type === 'folder') {
      const targetFolder = props.entity as IFolder;
      dispatch(setIsActiveContextMenu(false));
      dispatch(setDataDocumentTable(
        dataDocumentTable.map(element => ({
          ...element,
          isOpenContextMenu: false,
        }))
      ));
      dispatch(setFolderFilesCount(Number(targetFolder.documents_amount)));
      dispatch(openContextModal({
        entity: targetFolder,
        openTargetModal: () => dispatch(setIsOpenDeleteEntityModal(true))
      }));
    } else {
      dispatch(setFolderFilesCount(null));
      dispatch(openContextModal({
        entity: props.entity,
        openTargetModal: () => dispatch(setIsOpenDeleteEntityModal(true))
      }));
    }
  }
);

export const resetContextMenuField = createAsyncThunk(
  'resetContextMenuField/reset',
  async (props: {
  }, thunkApi) => {
    const { dispatch } = thunkApi;
    const rootState = thunkApi.getState() as RootState;
    const { dataDocumentTable } = rootState.documents;

    dispatch(setDataDocumentTable(dataDocumentTable.map(element => ({
      ...element,
      isOpenContextMenu: false,
    }))));
    dispatch(setIsActiveContextMenu(false));
  }
);

export const openContextModal = createAsyncThunk(
  'openContextModal/open',
  async (props: {
    entity: IFolder | IDocument,
    openTargetModal: () => void,
  }, thunkApi) => {
    const { dispatch } = thunkApi;
 
    dispatch(setEntityOfContextMenu(props.entity));
    dispatch(setSelectedDocument(props.entity as IDocument))
    props.openTargetModal();
    dispatch(resetContextMenuField({}));
  }
);

export const resetNotesState = createAsyncThunk(
  'resetNotesState/reset',
  async (_, thunkApi) => {
    const { dispatch } = thunkApi;

    dispatch(setSelectedNote(''));
    dispatch(setNotes([]));
    dispatch(setMoreInfoHidden(true));
  }
);

export const reloadSelectedRows = createAsyncThunk(
  'reloadSelectedRows/reset',
  async (props: {
  }, thunkApi) => {
    const { dispatch } = thunkApi;

    dispatch(setSelectedDocumentsIds([]));
    dispatch(setChoosenDocuments([]));
    dispatch(setSelectedRowKeys([]));
  }
);

export const closeEntityModal = createAsyncThunk(
  'closeEntityModal/reset',
  async (props: {
    navigate: NavigateFunction,
  }, thunkApi) => {
    const { dispatch } = thunkApi;
    
    dispatch(setIsOpenDeleteEntityModal(false));
    dispatch(setIsOpenMoveEntityModal(false));
    dispatch(setIsOpenInfoEntityModal(false));
    dispatch(setEntityOfContextMenu(null));
    dispatch(setSelectedDocument(null));
    dispatch(setIsOpenNewVersionModal(false));
    dispatch(setIsOpenRenameEntityModal(false));
    dispatch(setIsOpenShareEntityModal(false));
    dispatch(setNotes([]));
    dispatch(resetViewerSlice());
    dispatch(setFolderFilesCount(null));
    props.navigate(cleanDocumentViewUrl(window.location.pathname, new URLSearchParams(window.location.search)));
  }
);

export const closeUploadModal = createAsyncThunk(
  'closeUploadModal/reset',
  async (props: {
  }, thunkApi) => {
    const { dispatch } = thunkApi;

    dispatch(setIsVisibleUploadModal(false));
    dispatch(setIsAddedViaDragger(false));
    dispatch(setDocumentsType(null));
    dispatch(cleanFileList());
  }
);

export const handleMoveEntities = createAsyncThunk(
  'handleMoveEntities/move',
  async (props: {
    selectedFiles: string[],
    folder_id: string[],
    topLevelFolder: string,
    targetFolder?: string,
    t: TFunction,
    navigate: NavigateFunction,
  }, thunkApi) => {
    const { dispatch } = thunkApi;
    const rootState = thunkApi.getState() as RootState;
    const { dataRoom } = rootState.dataRoom;
    const { documents, folders, currentFolder, nestedDocuments, nestedFolders, expandedFoldersCache } = rootState.documents;
    const { userFoldersPermissions, foldersForPermissions, userRootPermissions } = rootState.permissions;
    const { messageApi } = rootState.windowState;

    const getPermissions = (id: string | null) => {
      if (!id) return userRootPermissions.permissions;
      return (
        userFoldersPermissions?.find(folder => folder.id === id)?.permissions ||
        foldersForPermissions?.find(folder => folder.id === id)?.permissions ||
        userRootPermissions.permissions
      );
    };

    const folderIdWithNullCase = props.folder_id[0] === 'null' ? null : props.folder_id[0];
    const currentFolderPermissions = getPermissions(currentFolder[0]?.id || null);
    const targetFolderPermissions = getPermissions(props.topLevelFolder !== 'null' ? props.topLevelFolder : null)

    const allDocsAndFolders = [...documents, ...nestedDocuments, ...folders, ...nestedFolders];
    const targetDocumentNames = allDocsAndFolders
      .filter(item => props.selectedFiles.includes(item.id!))
      .map(item => item.name);
    const targetFolderName = folders.find(folder => folder.id === props.folder_id[0])?.name || props.targetFolder || (folderIdWithNullCase === null ? props.t('Documents.modals.rootDir') : undefined);    
    const shortFolderName = (targetFolderName && targetFolderName?.length > 40) ? `${targetFolderName?.slice(0,40)}...` : targetFolderName;

    if (!targetFolderPermissions?.includes('can_upload') || !currentFolderPermissions?.includes('can_delete')) {
      messageApi?.error(`${props.t('Documents.error.accessToMove')}"${shortFolderName}"`)
      return;
    }
    
    dispatch(setIsLoadingProcess(true))
    try{
      const allFolderIds = [...folders, ...nestedFolders].map(f => f.id);
      const folderIdsToMove = props.selectedFiles.filter(id => allFolderIds.includes(id));
      const documentIdsToMove = props.selectedFiles.filter(id => !allFolderIds.includes(id));

      if (folderIdsToMove.length) {
        await api.moveFolders({ folder_ids: folderIdsToMove, room_id: dataRoom?.id!, target_folder_id: folderIdWithNullCase });
      }
      if (documentIdsToMove.length) {
        await api.addDocumentsToFolder({ document_ids: documentIdsToMove, folder_id: folderIdWithNullCase, room_id: dataRoom?.id! });
      }

      const allDocs = [...documents, ...nestedDocuments];
      const movedDocsList = allDocs.filter(doc => documentIdsToMove.includes(doc.id!));

      const sourceFolderCounts: Record<string, number> = {};
      movedDocsList.forEach(doc => {
        if (doc.folder_id) {
          sourceFolderCounts[doc.folder_id] = (sourceFolderCounts[doc.folder_id] || 0) + 1;
        }
      });

      const movedDocuments = documents.filter(document => !props.selectedFiles.includes(document.id!));
      const movedFolders = folders.filter(folder => !props.selectedFiles.includes(folder.id!));
      const movedNestedDocuments = nestedDocuments.filter(document => !props.selectedFiles.includes(document.id!));
      const movedNestedFolders = nestedFolders.filter(folder => !props.selectedFiles.includes(folder.id!));

      const updatedFolders = movedFolders.map(folder => {
        if (!folder.id) return folder;
        let amount = folder.documents_amount ?? 0;
        if (sourceFolderCounts[folder.id]) {
          amount = Math.max(0, amount - sourceFolderCounts[folder.id]);
        }
        if (folder.id === folderIdWithNullCase) {
          amount = amount + documentIdsToMove.length;
        }
        return { ...folder, documents_amount: amount };
      });

      const updatedNestedFolders = movedNestedFolders.map(folder => {
        if (!folder.id) return folder;
        let amount = folder.documents_amount ?? 0;
        if (sourceFolderCounts[folder.id]) {
          amount = Math.max(0, amount - sourceFolderCounts[folder.id]);
        }
        if (folder.id === folderIdWithNullCase) {
          amount = amount + documentIdsToMove.length;
        }
        return { ...folder, documents_amount: amount };
      });

      const movedIds = new Set(props.selectedFiles);
      const updatedCache: Record<string, (typeof expandedFoldersCache)[string]> = {};
      let cacheChanged = false;
      for (const [folderId, items] of Object.entries(expandedFoldersCache)) {
        const filtered = items.filter(item => !movedIds.has(item.id!));
        if (filtered.length !== items.length) cacheChanged = true;
        updatedCache[folderId] = filtered;
      }
      if (cacheChanged) dispatch(setExpandedFoldersCache(updatedCache));

      dataRoom && dispatch(setDocuments({documents: movedDocuments, folders: updatedFolders}));
      dispatch(setNestedDocumentsAndFolders({documents: movedNestedDocuments, folders: updatedNestedFolders}));
      dispatch(setLastMovedEvent({ targetFolderId: folderIdWithNullCase }));

      const isFolderMove = folderIdsToMove.length > 0;

      messageApi?.success(
        isFolderMove
          ? `${props.t('Documents.success.successMoveFolder')} '${shortFolderName}'`
          : `${props.t('Documents.success.successAddDocuments.before')} ${targetDocumentNames.join(', ')}
         ${props.t('Documents.success.successAddDocuments.after')} '${shortFolderName}'`
      );
    } catch (err) {
      const isFolderMove = props.selectedFiles.some(id => [...folders, ...nestedFolders].some(f => f.id === id));
      messageApi?.error(
        isFolderMove
          ? props.t('Documents.error.failedMoveFolder')
          : `${props.t('Documents.error.failedAddDocuments.before')} ${targetDocumentNames.join(', ')}
         ${props.t('Documents.error.failedAddDocuments.after')} '${shortFolderName}'`
      );
    } finally {
      dispatch(reloadSelectedRows({}));
      dispatch(resetNotesState())
      dispatch(closeEntityModal({navigate: props.navigate}));
      dispatch(setViewType('Table'));
      dispatch(setIsLoadingProcess(false));
      dispatch(setDocumentToViewNull());
    }
  }
);

export const handleDeleteChoosenObjects = createAsyncThunk(
  'handleDeleteChoosenObjects/delete',
  async (props: {
    forDeleteDocuments: any[],
    currentFolderId: string | null,
    documentList?: IDocument[],
    include_versions?: boolean,
    t: TFunction,
  }, thunkApi) => {
    const { dispatch } = thunkApi;
    const rootState = thunkApi.getState() as RootState;

    const { dataRoom } = rootState.dataRoom;
    const { documents, nestedDocuments, folders, nestedFolders } = rootState.documents;
    const { roomAllChats } = rootState.chat;
    const { available_rooms } = rootState.userData;
    const { messageApi } = rootState.windowState;

    const allDocuments = props.documentList || [...documents, ...nestedDocuments];
    const allFolders = [...folders, ...nestedFolders];
    const arrFilesIds: string[] = [];
    const arrFilesNames: string[] = [];
    const arrFolderIds: string[] = [];
    const arrFolderNames: string[] = [];
    const arrFilesConfidentialIds: string[] = [];
    const arrFilesConfidentialNames: string[] = [];    
    props.forDeleteDocuments.forEach((({ key: document }) => {    
      if (String(document).includes('folder')) {
        const folderId = String(document).split(' ')[1];
        const targetFolder = allFolders.find((folder) => folder.id === folderId);
        targetFolder && arrFolderIds.push(targetFolder?.id!)
        targetFolder && arrFolderNames.push(targetFolder?.name!)
      } else {
        const documentId = String(document).split(' ')[2];
        const targetDocument = allDocuments.find((doc) => doc.id === documentId);
        const chatsDeleteAt = roomAllChats.map((chat) => chat.entity_id === targetDocument?.id
          ? {...chat, deleted_at: dayjs().utc().format('YYYY-MM-DDTHH:mm:ss.SSSSSS')}
          : chat
        );
        dispatch(setRoomAllChats(chatsDeleteAt));

        if(targetDocument?.is_confidential){
          targetDocument && arrFilesConfidentialIds.push(targetDocument?.id!);
          targetDocument && arrFilesConfidentialNames.push(targetDocument?.name!);
        } else{
          targetDocument && arrFilesIds.push(targetDocument?.id!);
          targetDocument && arrFilesNames.push(targetDocument?.name!);
        }
      }
    }));
    
    dispatch(setIsDeletingDocuments(true))
    try{      
      arrFilesIds.length && await api.deleteDocuments(arrFilesIds, {
        room_id: dataRoom?.id!,
        include_versions: props.include_versions,
        action: 'delete',
        details: {
          documents_names: arrFilesNames.join(', '),
          documents_ids: arrFilesIds.join(', ')
        }
      });
      arrFilesConfidentialIds.length && await api.deleteDocuments(arrFilesConfidentialIds, {
        room_id: dataRoom?.id!,
        action: 'delete_confident',
        include_versions: props.include_versions,
        details: {
          documents_names: arrFilesConfidentialNames.join(', '),
          documents_ids: arrFilesConfidentialIds.join(', ')
        }
      });
      
      await Promise.all(arrFolderIds.map(async(id) => await api.deleteFolder(id)));
      const newFolders = folders.filter(folder => !arrFolderIds.includes(folder.id!));
      const newDocuments = documents.filter(doc => !arrFilesIds.includes(doc.id!) && !arrFilesConfidentialIds.includes(doc.id!)); 
      
      const newNestedFolders = nestedFolders.filter(folder => !arrFolderIds.includes(folder.id!));
      const newNestedDocuments = nestedDocuments.filter(doc => !arrFilesIds.includes(doc.id!) && !arrFilesConfidentialIds.includes(doc.id!));

      dispatch(setDocuments({documents: newDocuments, folders: newFolders}));
      dispatch(setNestedDocumentsAndFolders({documents: newNestedDocuments, folders: newNestedFolders}));
      dispatch(setDataRoom({...dataRoom!, folders: newFolders}));
      props.currentFolderId === null && dispatch(setFoldersForPermissions(newFolders));
      
      const deletedDocumentsCount = arrFilesIds.length + arrFilesConfidentialIds.length;
      if (deletedDocumentsCount > 0) {
        dispatch(
          setRooms(
            available_rooms.map((room) =>
              room.id === dataRoom?.id
                ? {
                    ...room,
                    documents_amount: Math.max(
                      0,
                      (room.documents_amount || 0) - deletedDocumentsCount
                    ),
                  }
                : room
            )
          )
        );
      }
      messageApi?.success(`${props.t('Documents.success.deleteObjects')}: ${[...arrFilesNames, ...arrFolderNames, ...arrFilesConfidentialNames].join(', ')}`);
    } catch (err) {
      messageApi?.error(`${props.t('Documents.error.deleteObjects')}: ${[...arrFilesNames, ...arrFolderNames, ...arrFilesConfidentialNames].join(', ')}`);
    } finally {
      dispatch(reloadSelectedRows({}));
      dispatch(resetNotesState());
      dispatch(setIsDeletingDocuments(false))
    }
  }
);

const handleDownloadCases = (payload: IDocumentInfo, language: string): Promise<AxiosResponse<any, any>> => {
  let response: Promise<AxiosResponse<any, any>>;
  const isPdfFile = payload.extension?.toLowerCase() === 'pdf';
  const needsConversion = payload.is_webview || payload.signing_status !== null;
    
  if (isPdfFile && !needsConversion) {
    response = api.getConvertedFile({...payload, locale: language});
  } else if (needsConversion && !isPdfFile) {
    response = api.convertRequest({...payload, is_webview: false, locale: language});
  } else {
    response = api.downloadFile(payload);
  }
    
  return response as Promise<AxiosResponse<Blob, any>>
};

export const handleDownload = createAsyncThunk(
  'handleDownload/download',
  async (props: {
    is_webview: boolean,
    language: string,
    t: TFunction,
    messageApi: MessageInstance,
    targetDocument?: {
      entity: IDocument | IFolder;
      version: IDocument;
    },
  }, thunkApi) => {
    const { dispatch } = thunkApi;
    const rootState = thunkApi.getState() as RootState;

    const { dataRoom } = rootState.dataRoom;
    const { documents, nestedDocuments, choosenDocuments } = rootState.documents;
    const allDocuments = [...documents, ...nestedDocuments];

    let arrWithTimeStamps = [0];
    const delay = 300;

    const requests = props.targetDocument
      ? (() => {
          const { entity, version } = props.targetDocument;
          const shouldDownloadSigned = version?.signing_type === 'qes';
          const correctFileName = props.is_webview ? version.name.replace(/\.\w+$/, '.pdf') : version.name;
          return [{
            extension: version.extension,
            token: version.token,
            id: dataRoom?.id,
            entity: version.id,
            action: version.is_confidential ? 'download_confident' : 'download',
            is_agreement: false,
            is_webview: props.is_webview,
            signing_status: version.signing_status,
            name: correctFileName,
            ...(shouldDownloadSigned && { as_signed: true }),
          }];
        })()
      : choosenDocuments.map(({ key: documentKey }) => {
          const documentInfo = (documentKey as string).split(' ');
          const documentToken = documentInfo[1];
          if(documentInfo[0] === 'folder') return null;

          const foundDoc = allDocuments.find(d => d.token === documentToken);
          if (!foundDoc) return null;

          const { extension, name, id, is_confidential, signing_status, signing_type} = foundDoc;
          const isQesSigned = signing_type === 'qes';
          return {
            extension,
            token: documentToken,
            id: dataRoom?.id,
            entity: id,
            action: is_confidential ? 'download_confident' : 'download',
            is_agreement: false,
            is_webview: props.is_webview,
            signing_status,
            name: props.is_webview ? name.replace(/\.\w+$/, '.pdf') : name,
            ...(isQesSigned && { as_signed: true }),
          }
        }).filter((el) => el !== null) as (IDocumentInfo & { name: string })[];

    const allRequests = requests.map(el => {
      dispatch(addFileForDownloading(el as IDocumentInfo));
      return handleDownloadCases(el as IDocumentInfo, props.language);
    });
    allRequests.forEach( async (request, i) => {      
      try {
        let file: any;
        const response = await request;
         if (response.data.status === 'ready') {
          const requestPayload = JSON.parse(response.config.data);
          const converted = await api.getConvertedFile(requestPayload);
          file = converted.data;
          dispatch(removeDownloadedFile(requests[i]?.token || ''));
        } else if (response?.data.status === 'in_progress') { 
          return;
        } else {
          file = response.data
          dispatch(removeDownloadedFile(requests[i]?.token || ''));
        };

        let delayCurrent = 0;
        const currentTimestamp = new Date().getTime();
        const theLagestTS = [...arrWithTimeStamps].sort().reverse()[0];
        // разница между этими двумя значениями должна быть равна минимум delay
        // иначе нужно откладывать скачивания файла через setTimeout на значени delay
        // при отрицательной разнице к delay прибавляем разницу
        if (currentTimestamp - theLagestTS < 0) {
          arrWithTimeStamps.push(currentTimestamp + (delay + (theLagestTS - currentTimestamp)));
          delayCurrent = delay + (theLagestTS - currentTimestamp);
        } else if (currentTimestamp - theLagestTS < delay) {
          arrWithTimeStamps.push(currentTimestamp + delay);
          delayCurrent = delay;
        } else {
          arrWithTimeStamps.push(currentTimestamp);
        };        
      
        return setTimeout(() => {          
          const url = window.URL.createObjectURL(file);
          const a = document.createElement('a');
          a.href = url;
          a.download = requests[i]?.name!;
          document.body.appendChild(a);
          a.click();
          a.remove();
          getDownloadMessage(requests[i]?.name!, true, props.t, props.messageApi)
        }, delayCurrent);
      } catch (e) {
        console.log(e);
        
        dispatch(removeDownloadedFile(requests[i]?.token || ''));
        getDownloadMessage(requests[i]?.name!, false, props.t, props.messageApi)
      }
    });
      dispatch(reloadSelectedRows({}));
  }
);

export const handleBulkDownloadArchive = createAsyncThunk(
  'handleBulkDownloadArchive/download',
  async (props: {
    t: TFunction,
    messageApi: MessageInstance,
    entity?: any,
  }, thunkApi) => {
    const { dispatch } = thunkApi;
    const rootState = thunkApi.getState() as RootState;

    const { documents, nestedDocuments, choosenDocuments } = rootState.documents;
    const allDocuments = [...documents, ...nestedDocuments];

    const document_ids: string[] = [];
    const folder_ids: string[] = [];

    if (!choosenDocuments[0] && props.entity.type === 'folder') {
      folder_ids.push(props.entity.id);
      dispatch(addFileForDownloading({token: props.entity.id} as IDocumentInfo));
    }

    choosenDocuments.forEach(({ key: documentKey }) => {
      const documentInfo = (documentKey as string).split(' ');
      if (documentInfo[0] === 'folder') {
        const folderId = documentInfo[1];
        folder_ids.push(folderId);
        dispatch(addFileForDownloading({token: folderId!} as IDocumentInfo));
      } else {
        const documentToken = documentInfo[1];
        const document = allDocuments.find(d => d.token === documentToken);
        const docId = document?.id || documentInfo[2];
        if (docId) {
          document_ids.push(docId);
          dispatch(addFileForDownloading({token: docId} as IDocumentInfo));
        }
      }
    });

    const payload: { document_ids?: string[]; folder_ids?: string[] } = {};
    if (document_ids.length > 0) payload.document_ids = document_ids;
    if (folder_ids.length > 0) payload.folder_ids = folder_ids;
    
    try {
      const response = await api.bulkDownloadArchive(payload);
      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = response.headers['content-disposition'].split('filename=')[1];
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      props.messageApi?.success(`${props.t('Documents.contextMenu.successDownloadArchive')}`);
    } catch (error) {
      props.messageApi?.error(`${props.t('Documents.contextMenu.errorDownload')}`);
    } finally {
      dispatch(reloadSelectedRows({}));
      [...document_ids, ...folder_ids].forEach((el) => dispatch(removeDownloadedFile(el)));
    }
  }
);

export const handleUploadFiles = createAsyncThunk(
  'handleUploadFiles/upload',
  async (props: {
    otherFileList?: RcFile[];
    t: TFunction;
    navigate: NavigateFunction;
  }, thunkApi) => {
    const { dispatch } = thunkApi;
    const rootState = thunkApi.getState() as RootState;    
    
    const { userFoldersPermissions, foldersForPermissions, userRootPermissions } = rootState.permissions;
    const { fileList, currentFolder, documentsType, folders, documents } = rootState.documents;
    const { dataRoom } = rootState.dataRoom;
    const { userData } = rootState.userData;
    const { messageApi } = rootState.windowState;

    const fileCase = !documentsType ? 'files' : documentsType;
    const uploadFilesCases = {
      files: {
        title: props.t('Documents.modals.upload.files'),
        isConfidential: false,
      },
      confidentialFiles: {
        title: props.t('Documents.modals.upload.confidentialFiles'),
        isConfidential: true,
      }
    };

    const parentFolderID = currentFolder[0] ? currentFolder[0]?.id : null;

    const currentFolderWithPermissions = parentFolderID 
      ? userFoldersPermissions?.find(folder => folder.id === parentFolderID) || foldersForPermissions?.find(folder => folder.id === parentFolderID)
      : userRootPermissions;

    messageApi?.destroy()
    const targetFileList = props.otherFileList || fileList;    
    const currentFolderId = currentFolder[0] ? currentFolder[currentFolder.length - 1]?.id : null;
    const uploadRoomId = dataRoom?.id;

    const clientFolders = (targetFileList as IWithWebkitPath[]).filter((file) => file.webkitRelativePath?.includes('/'));
    const files = (targetFileList as IWithWebkitPath[]).filter((file) => !file.webkitRelativePath?.includes('/'));
    const abortController = new AbortController();
  
    dispatch(setUploadAbortController(abortController))
    dispatch(closeUploadModal({}));

    if(clientFolders.length > 0){
      const segregated = segregateToRootFolders(clientFolders);

      segregated.forEach(async (filePathList) => {
        const buildedFolders = buildFoldersTree(filePathList);
        const newFolderFilePath = filePathList[0];
        const newFolderPath = newFolderFilePath.join('/');
        const newFolderFirstFile = clientFolders.find((el) => el.webkitRelativePath === newFolderPath);

        const tempFolderId = `temp_folder_${crypto.randomUUID()}`;
        const tempDocId = `temp_${crypto.randomUUID()}`;
        const folderName = newFolderFilePath[0];
        
        const tempFolder: IFolder = {
          id: tempFolderId,
          name: folderName,
          parent_folder_id: currentFolder || null,
          room_id: dataRoom?.id,
          type: 'folder',
        };
        
        dispatch(setUploadingFolders(tempFolder));
        const firstFolderFormData = new FormData();
        firstFolderFormData.append('json_template', JSON.stringify(buildedFolders));
        firstFolderFormData.append('room_id', dataRoom?.id!);
        firstFolderFormData.append('document_path', newFolderFilePath.slice(0, newFolderFilePath.length - 1).join('/'));
        firstFolderFormData.append('is_new_folder', `${true}`);
        if (currentFolderId) firstFolderFormData.append('parent_folder_id', `${currentFolderId}`);
        if (uploadFilesCases[fileCase]?.isConfidential) firstFolderFormData.append('is_confidential', `${true}`);
        firstFolderFormData.append('document', newFolderFirstFile as RcFile);

        try{
          const response = await api.uploadFolder({
            formData: firstFolderFormData,
            name: String(newFolderFirstFile?.name),
            action: uploadFilesCases[fileCase]?.isConfidential ? 'create_confident' : 'create',
            signal: abortController.signal,
            onUploadProgress(progressEvent) {
              if (progressEvent.total) {
                const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                dispatch(
                  setUploadingDocuments({
                    document_id: tempDocId,
                    document_name: String(newFolderFirstFile?.name),
                    uploadProgress: percentCompleted,
                    root_folder: {
                      id: tempFolderId, 
                      name: folderName
                    }
                  })
                );
              }
            },
          });
          const uploadedClienFolder: IFolder = {
            id: response.data.root_folder?.id,
            owner_id: userData?.id, 
            name: response.data.root_folder?.name || '',
            key: response.data.root_folder?.id,
            parent_folder_id: currentFolder || null,
            isOpenContextMenu: false,
            clicked: false,
            room_id: dataRoom?.id,
            permissions: currentFolderWithPermissions?.permissions, 
            type: 'folder',
          }
          const uploadedFolders: IFolder[] = [...folders, uploadedClienFolder];
          
          
          dispatch(setUploadingFolders({
            ...uploadedClienFolder,
            old_folder_id: tempFolderId
          }));
          
          dispatch(
            setUploadingDocuments({
              document_id: response.data.id,
              document_name: response.data.name,
              root_folder: response.data.root_folder,
              room_id: dataRoom?.id,
              old_document_id: tempDocId,
            })
          );
          dispatch(addNewFolder(uploadedClienFolder));
          dispatch(setUserPermissions({documents: documents, folders: uploadedFolders, rootFolder: userRootPermissions}));
        } catch(err: any) {
          if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') {
            dispatch(setCanceledUploads(tempFolderId));
          } else{
            dispatch(setUploadingDocuments({
              document_id: tempDocId,
              status: 'FAILED',
              room_id: dataRoom?.id,
            }))
          }
          
          messageApi?.error(props.t('Documents.error.uploadDocument'));
          if(filePathList === segregated[segregated.length - 1]){
            dispatch(setIsUploadingDocuments(false));
            dispatch(setIsVisibleUploadModal(false));
          }
          return;
        };
        await Promise.all(
          filePathList.map((filePath) => {
            if (filePathList[0] === filePath) return undefined;
            const path = filePath.join('/');
            const file = clientFolders.find(
              (el) => el.webkitRelativePath === path
            );
            const tempDocId = `temp_${file?.uid}`;
            const formData = new FormData();
            formData.append('json_template', JSON.stringify(buildedFolders));
            formData.append('room_id', dataRoom?.id!);
            formData.append(
              'document_path',
              filePath.slice(0, filePath.length - 1).join('/')
            );
            formData.append('is_new_folder', `${false}`);
            if (currentFolderId)
              formData.append('parent_folder_id', `${currentFolderId}`);
            if (uploadFilesCases[fileCase]?.isConfidential)
              formData.append('is_confidential', `${true}`);
            formData.append('document', file as RcFile);
            // currentFolderId && formData.append('folder_id', currentFolderId!);
            return api.uploadFolder({
              formData: formData,
              name: file?.name || '',
              action: uploadFilesCases[fileCase]?.isConfidential
                ? 'create_confident'
                : 'create',
              signal: abortController.signal,
              onUploadProgress(progressEvent) {
                if (progressEvent.total) {
                  const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                  dispatch(
                    setUploadingDocuments({
                      document_id: tempDocId,
                      document_name: String(file?.name),
                      uploadProgress: percentCompleted,
                      root_folder: {
                        id:tempFolderId,
                        name:folderName
                      }
                    })
                  );
                }
              },
            });
          })
        )
          .then((values) => {
            values.filter(Boolean).forEach((response) => {
              const file = filePathList.find(
                filePath => filePath.join('/') === response?.config?.data?.get('document_path') + '/' + response?.config?.data?.get('document')?.name
              );
              const fileObj = clientFolders.find(
                (el) => el.webkitRelativePath === file?.join('/')
              );
              const tempDocId = `temp_${fileObj?.uid}`;
              
              if (response && response.data) {
                dispatch(
                  setUploadingDocuments({
                    document_id: response.data.id,
                    old_document_id: tempDocId,
                    document_name: response.data.name,
                    root_folder: response.data.root_folder,
                  })
                );
              }
            });
          })
         .catch((error) => {
            if (error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') {
              dispatch(setCanceledUploads(tempFolderId));
            }
            messageApi?.error(props.t('Documents.error.uploadDocument'));
            dispatch(setIsVisibleUploadModal(false));
          })
          .finally(() => {
            dispatch(setIsUploadingDocuments(false));
          });
      })
    };

    if(files.length > 0) {
      const tempDocIds = files.map(file => `temp_${file.uid}`);
      
      await Promise.all(files.map((file, index) => {
        const formData = new FormData();
        formData.append('document', file as RcFile);
        formData.append('room', dataRoom?.id!);
        if (currentFolderId) formData.append('folder_id', currentFolderId);
        if (uploadFilesCases[fileCase]?.isConfidential) formData.append('is_confidential', `${true}`);

        const tempDocId = tempDocIds[index];
        return api.uploadFile({
          formData: formData,
          id: dataRoom?.id!,
          name: file.name,
          action: uploadFilesCases[fileCase]?.isConfidential ? 'create_confident' : 'create',
          signal: abortController.signal,
          onUploadProgress(progressEvent) {
            if (progressEvent.total) {
              const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              dispatch(
                setUploadingDocuments({
                  document_id: tempDocId,
                  document_name: file.name,
                  uploadProgress: percentCompleted,
                  room_id: dataRoom?.id,
                })
              );
            }
          },
        }).then(response => {
          dispatch(
            setUploadingDocuments({
              document_id: response.data.id,
              old_document_id: tempDocId,
              document_name: response.data.name,
              room_id: dataRoom?.id,
              extension: response.data.extension
            })
          );
          
          dispatch(fetchDocumentsData({
            type: 'add', 
            newDocuments: [response.data], 
            navigate: props.navigate,
            roomId: uploadRoomId
          }));

          return response;
        })
        .catch(error => {
          dispatch(setUploadingDocuments({
            document_id: tempDocId,
            document_name: file.name,
            status: 'FAILED',
            room_id: dataRoom?.id,
          }))
          return error;
        })
      }))
      .then(values => {
        const responseRoomId = values.length > 0 && values[0].data.room_id ? values[0].data.room_id : uploadRoomId;        
        if (responseRoomId === dataRoom?.id) {
          messageApi?.success(props.t('Documents.success.uploadDocument'));
        }
        dispatch(closeUploadModal({}));
      })
      .catch((error: any) => {
        dispatch(setIsVisibleUploadModal(false));
        if (error?.response?.data?.message === 'Room has namesakes documents'){
          messageApi?.error(props.t('Documents.error.dublicate'));
          return
        }

        if (error?.response?.status === 413){
          messageApi?.error(props.t('Documents.error.uploadDocumentSize'),7);
          return
        }
        
        if (error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') {
          dispatch(setCanceledUploads(tempDocIds));
        }

        messageApi?.error(props.t('Documents.error.uploadDocument'));
      }).finally(() => {
        dispatch(setIsUploadingDocuments(false));
      })
    };
  }
);

export const fetchDocumentFilters = createAsyncThunk(
  'dataDocuments/fetchDocumentFilters',
  async (roomId: string, { dispatch }) => {
    try {
      const res = await api.getDocumentFilters(roomId);
      const statusFilter = res.data.find((filter: { column: string; }) => filter.column === 'status');
      dispatch(setStatusFilterOptions(statusFilter?.filters || []));
    } catch {
      dispatch(setStatusFilterOptions([]));
    }
  }
);

export const applyDocumentFilters = createAsyncThunk(
  'dataDocuments/applyDocumentFilters',
  async (
    props: {
      roomId: string;
      folderId: string | null;
      appliedFilters: DocumentFilters;
      rawFilters: Record<string, any>;
    },
    { dispatch }
  ) => {
    const statusValues = (props.rawFilters.status as DocumentFilterKey[]) || [];
    const nextFilters: DocumentFilters = { ...props.appliedFilters, status: statusValues };

    dispatch(setAppliedFilters(nextFilters));
    return dispatch(fetchAvailableDocuments({
      roomId: props.roomId,
      folderId: props.folderId,
      filters: nextFilters,
    }));
  }
);

